import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MessagesSquare, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Carregando, Vazio } from '../../componentes/estados';
import { useSessao } from '../../nucleo/sessao';
import { ListaCanais, type EstadoLista } from './ListaCanais';
import { ExplorarCanais, NovaConversa } from './Modais';
import { PainelCanal } from './PainelCanal';

/**
 * Fase 2, W15: chat da equipe, no mesmo desenho da caixa de Conversas: lista à esquerda e conversa à direita. No celular
 * são duas telas, e o canal aberto fica na URL (`?canal=<id>`), o que também serve de link do aviso, do push e do botão
 * "Discutir com a equipe" do chatter.
 */
export function Chat() {
  const { t } = useTranslation('equipe');
  const s = useSessao();
  const [params, setParams] = useSearchParams();
  const id = params.get('canal');
  const [lista, setLista] = useState<EstadoLista>('carregando');
  const [nova, setNova] = useState(false);
  const [explorar, setExplorar] = useState(false);
  const aoEstadoLista = useCallback((e: EstadoLista) => setLista((anterior) => (e === 'carregando' && anterior === 'ok' ? 'ok' : e)), []);
  const abrir = useCallback((canal: string) => setParams({ canal }, { replace: false }), [setParams]);
  const podeEnviar = s.pode('equipe.enviar');

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* Review r17 visual M2 (F6, um primário por vista): com uma conversa aberta, o primário é o "Enviar" do compositor e o
          "Nova conversa" do cabeçalho fica secundário; sem conversa aberta, ele é o único primário (o do vazio é secundário). */}
      {podeEnviar && (
        <AcaoPrimaria>
          <Botao variante={id ? 'secundario' : 'primario'} icone={<Plus className="size-4" />} onClick={() => setNova(true)}>
            {t('acoes.novaConversa')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="grid min-w-0 gap-3 md:h-168 md:grid-cols-5">
        <section aria-label={t('lista.conversas')} className={`h-144 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-borda bg-sup-1 md:col-span-2 md:flex md:h-full ${id ? 'hidden' : 'flex'}`}>
          <ListaCanais selecionado={id} aoEscolher={abrir} aoEstado={aoEstadoLista} aoExplorar={() => setExplorar(true)} aoNova={podeEnviar ? () => setNova(true) : undefined} />
        </section>
        <section className={`h-144 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-borda bg-sup-1 md:col-span-3 md:flex md:h-full ${id ? 'flex' : 'hidden'}`}>
          {id ? (
            <PainelCanal key={id} canalId={id} aoVoltar={() => setParams({}, { replace: true })} aoSaiu={() => setParams({}, { replace: true })} />
          ) : lista === 'ok' ? (
            <Vazio icone={<MessagesSquare className="size-5" aria-hidden />} titulo={t('painel.escolhaTitulo')} texto={t('painel.escolha')} />
          ) : lista === 'carregando' ? (
            <Carregando />
          ) : null}
        </section>
      </div>
      <NovaConversa aberto={nova} aoFechar={() => setNova(false)} aoAbrir={abrir} />
      <ExplorarCanais aberto={explorar} aoFechar={() => setExplorar(false)} aoAbrir={abrir} />
    </div>
  );
}
